// src/pages/Login.tsx
import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate()

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
  
    setLoading(true);
    setError('');
  
    try {
      const { data, error: signInError } =
        await supabase.auth.signInWithPassword({
          email: email.trim(),
          password,
        });
  
      if (signInError) {
        throw signInError;
      }
  
      if (!data.user) {
        throw new Error('Unable to retrieve user information.');
      }
  
      // Verify admin privileges
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('is_admin')
        .eq('id', data.user.id)
        .single();
  
      if (profileError) {
        console.error('Profile lookup error:', profileError);
        await supabase.auth.signOut();
        throw new Error('Unable to verify account permissions.');
      }
  
      if (!profile?.is_admin) {
        await supabase.auth.signOut();
        throw new Error('Access denied. Admin privileges required.');
      }
  
      console.log('Admin login successful:', data.user.email);
  
      // Redirect to dashboard/home
      navigate('/', { replace: true });
  
    } catch (err: any) {
      console.error('Login error:', err);
  
      setError(
        err?.message ||
        'An unexpected error occurred. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Logo */}
        <div style={styles.logo}>
          <img src='/logo1.png' alt='Zorax' style={{width:150}} />
          <span style={styles.adminBadge}>Admin</span>
        </div>
        <p style={styles.subtitle}>Sign in to the admin dashboard</p>

        {error && <div style={styles.errorBox}>{error}</div>}

        <form onSubmit={handleLogin} style={styles.form}>
          <div style={styles.field}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@zoraxlabs.com"
              style={styles.input}
              required
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              style={styles.input}
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            style={{ ...styles.button, opacity: loading ? 0.7 : 1 }}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <p style={styles.footer}>
          Zorax Admin · Restricted Access
        </p>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f0eeff 0%, #faf9ff 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  card: {
    background: '#fff',
    borderRadius: '20px',
    padding: '48px 40px',
    width: '100%',
    maxWidth: '400px',
    boxShadow: '0 20px 60px rgba(108, 95, 252, 0.12)',
    border: '1px solid #ebe8ff',
  },
  logo: {
    fontSize: '28px',
    fontWeight: 800,
    color: '#1a1a1a',
    letterSpacing: '-0.05em',
    marginBottom: '6px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  adminBadge: {
    fontSize: '11px',
    fontWeight: 700,
    background: '#d0e4d0',
    color: '#5c7a5c',
    padding: '3px 10px',
    borderRadius: '20px',
    letterSpacing: '0.5px',
    textTransform: 'uppercase' as const,
  },
  subtitle: {
    fontSize: '14px',
    color: '#888',
    marginBottom: '28px',
  },
  errorBox: {
    background: '#fde8e8',
    border: '1px solid #f16d75',
    borderRadius: '10px',
    padding: '12px 16px',
    fontSize: '13px',
    color: '#c0392b',
    marginBottom: '20px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
  },
  label: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#444',
  },
  input: {
    padding: '12px 16px',
    borderRadius: '10px',
    border: '1px solid #e0e0e0',
    fontSize: '14px',
    color: '#1a1a1a',
    outline: 'none',
    fontFamily: 'inherit',
    transition: 'border-color 0.2s',
  },
  button: {
    background: '#7a5c7a',
    color: '#fff',
    border: 'none',
    padding: '14px',
    borderRadius: '10px',
    fontSize: '15px',
    fontWeight: 700,
    cursor: 'pointer',
    marginTop: '4px',
    fontFamily: 'inherit',
  },
  footer: {
    fontSize: '12px',
    color: '#bbb',
    textAlign: 'center' as const,
    marginTop: '24px',
  },
};